/* eslint-disable react/prop-types */
import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { isEmpty, contains, map } from 'underscore';
import { TextField } from '@fluentui/react/lib/TextField';
import { SmartPhoneNumberEditor } from '@bingads-webui-campaign-react/smart-component-text';
import {
  DATA_SOURCE_KEY_MAPPING,
  isValidUrl,
  prependProtocolToUrl,
  validateEmailAddress,
} from '@bingads-webui-campaign-react/smart-component-util';

import {
  SmartActionButtonTypes,
  EMAIL_ACTION_TYPES,
} from './utils/smart-action-types';
import { EmailForm } from './components/email-form';
import { SmartBookingManagement } from './components/booking-management';

// Smart Phone Editor
const PhoneEditor = ({
  i18n,
  classes,
}) => useMemo(() => (
  <div className={classes.group}>
    <SmartPhoneNumberEditor
      className="smart-action-button-phone-input"
      i18n={i18n}
      dataSourceName={DATA_SOURCE_KEY_MAPPING.PhoneNumber}
    />
  </div>
), [classes.group, i18n]);

// Editor for those value come from DataSource
const DataSourceFieldEditor = ({
  classes,
  selectedActionType,
  editorLabel,
  dataSourceValue,
  readProcessor,
  writeProcessor,
  dispatchSiteConfig,
  validateInput,
}) => {
  const value = readProcessor(dataSourceValue);

  const dispatchDataSourceChange = useCallback((action, newValue) => {
    const { dataSourceName } = SmartActionButtonTypes[action];
    const formattedValue = writeProcessor(newValue);

    dispatchSiteConfig({
      type: 'UPDATE_DATASOURCE_BY_NAME',
      payload: {
        name: dataSourceName,
        value: formattedValue,
      },
    });
  }, [dispatchSiteConfig, writeProcessor]);

  const onDataSourceChange = useCallback((e, val) => {
    dispatchDataSourceChange(selectedActionType.key, val);
  }, [dispatchDataSourceChange, selectedActionType.key]);

  return useMemo(() => (
    <div className={classes.group}>
      <TextField
        className="smart-action-button-data-source-input"
        label={editorLabel}
        value={value}
        onChange={onDataSourceChange}
        onGetErrorMessage={validateInput}
      />
    </div>
  ), [classes.group, editorLabel, onDataSourceChange, validateInput, value]);
};

const LinkTextEditor = ({
  classes,
  i18n,
  linkText,
  validateInput,
  componentConfig,
  dispatchSiteConfig,
}) => {
  const onLinkTextChange = useCallback((e, val) => {
    const newComponentConfig = {
      ...componentConfig,
      linkText: val,
    };

    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: newComponentConfig,
    });
  }, [componentConfig, dispatchSiteConfig]);

  return useMemo(() => (
    <div className={classes.group}>
      <TextField
        className="smart-action-button-text-input"
        label={i18n.getString(_TL_('Button text'))}
        value={linkText}
        onChange={onLinkTextChange}
        onGetErrorMessage={validateInput}
      />
    </div>
  ), [classes.group, i18n, linkText, onLinkTextChange, validateInput]);
};

const LinkUrlEditor = ({
  classes,
  i18n,
  linkUrl,
  componentConfig,
  dispatchSiteConfig,
}) => {
  const validateLink = useCallback((val) => {
    if (isValidUrl(val)) {
      return '';
    }
    return i18n.getString(_TL_('Invalid link'));
  }, [i18n]);

  const onLinkUrlChange = useCallback((e, val) => {
    const newComponentConfig = {
      ...componentConfig,
      linkUrl: val,
    };

    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: newComponentConfig,
    });
  }, [componentConfig, dispatchSiteConfig]);

  const onLinkUrlBlur = useCallback((e) => {
    onLinkUrlChange(e, prependProtocolToUrl(linkUrl));
  }, [linkUrl, onLinkUrlChange]);

  return useMemo(() => (
    <div className={classes.group}>
      <TextField
        className="smart-action-button-url-input"
        label={i18n.getString(_TL_('Link'))}
        placeholder="http://link"
        value={linkUrl}
        onChange={onLinkUrlChange}
        onGetErrorMessage={validateLink}
        onBlur={onLinkUrlBlur}
      />
    </div>
  ), [classes.group, i18n, linkUrl, onLinkUrlBlur, onLinkUrlChange, validateLink]);
};

const PreviewForm = ({
  classes,
  i18n,
  selectedActionType,
}) => {
  const [hideDialog, setHideDialog] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  const openDialog = useCallback(() => {
    setHideDialog(prev => (prev ? false : prev));
  }, []);

  const onSubmitForm = useCallback(() => {
    setShowWarning(true);
  }, []);

  const onCancelForm = useCallback(() => {
    setShowWarning(false);
    setHideDialog(true);
  }, []);

  return useMemo(() => (
    <div className={classes.group}>
      <button
        className={classNames(classes.previewLink, 'preview-link')}
        onClick={openDialog}
      >
        {i18n.getString(_TL_('Preview form'))}
      </button>
      <EmailForm
        title={selectedActionType.text}
        hideDialog={hideDialog}
        showError={false}
        showWarning={showWarning}
        onSubmit={onSubmitForm}
        onCancel={onCancelForm}
        i18n={i18n}
      />
    </div>
  ), [
    classes.group, classes.previewLink, hideDialog, i18n,
    onCancelForm, onSubmitForm, openDialog, selectedActionType.text,
    showWarning,
  ]);
};

const BookingOperations = ({
  i18n,
  classes,
  operations,
}) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [
    bookingOperation,
    setBookingOperation,
  ] = useState(SmartActionButtonTypes.BookAppointment.operations[0]);

  const onOpenBookingModal = useCallback((e, index) => {
    setIsBookingModalOpen(true);

    const selectedBookingOperation = operations[index];
    setBookingOperation(selectedBookingOperation);
  }, [operations]);

  const onCloseBookingModal = useCallback(() => {
    setIsBookingModalOpen(false);
  }, []);

  return useMemo(() => (
    <div>
      {
        operations.map(({ title }, index) => (
          <div className={classes.group} key={title}>
            <button
              className={classNames(classes.previewLink, 'booking-link')}
              onClick={e => onOpenBookingModal(e, index)}
            >
              {i18n.getString(title)}
            </button>
          </div>
        ))
      }
      <SmartBookingManagement
        isOpen={isBookingModalOpen}
        i18n={i18n}
        title={bookingOperation.title}
        actionButtonLabel={bookingOperation.action}
        onCloseModal={onCloseBookingModal}
      />
    </div>
  ), [
    bookingOperation.action, bookingOperation.title, classes.group, classes.previewLink,
    i18n, isBookingModalOpen, onCloseBookingModal, onOpenBookingModal, operations,
  ]);
};

const BookingAppointment = (props) => {
  const { isSmartPageBookAppointmentEnabled } = props.pilots;

  if (!isSmartPageBookAppointmentEnabled) {
    return <LinkUrlEditor {...props} />;
  }

  return (
    <React.Fragment>
      <DataSourceFieldEditor {...props} />
      <LinkUrlEditor {...props} />
      <BookingOperations {...props} />
    </React.Fragment>
  );
};

const actionTypeToEditorMapping = {
  [SmartActionButtonTypes.CallNow.name]: [PhoneEditor],
  [SmartActionButtonTypes.SendMail.name]: [DataSourceFieldEditor],
  [SmartActionButtonTypes.GetQuote.name]: [DataSourceFieldEditor, PreviewForm],
  [SmartActionButtonTypes.ContactUs.name]: [DataSourceFieldEditor, PreviewForm],
  [SmartActionButtonTypes.CustomLink.name]: [LinkTextEditor, LinkUrlEditor],
  [SmartActionButtonTypes.BookAppointment.name]: [BookingAppointment],
};

export const ActionCustomEditor = (props) => {
  const {
    i18n,
    selectedActionType,
  } = props;

  const validateInput = useCallback((val) => {
    if (contains(EMAIL_ACTION_TYPES, selectedActionType.key) &&
      !validateEmailAddress(val)) {
      return i18n.getString(_TL_('Invalid email address'));
    }

    if (isEmpty(val)) {
      return i18n.getString(_TL_('Cannot be empty'));
    }

    return '';
  }, [i18n, selectedActionType.key]);

  const customEditors = actionTypeToEditorMapping[selectedActionType.key];

  if (isEmpty(customEditors)) {
    return null;
  }

  return map(customEditors, CustomEditor => (
    <CustomEditor validateInput={validateInput} {...props} key={CustomEditor.name} />
  ));
};
